import { Injectable } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import {
  SubscriptionService,
  SubscriptionUsageAlert,
  SubscriptionUsageModel,
} from "./subscription.service";
import { TokenService } from "./token.service";

const ALERT_STORAGE_PREFIX = "flowchest-usage-alert";

@Injectable({ providedIn: "root" })
export class SubscriptionUsageAlertService {
  private checkInFlight = false;

  constructor(
    private subscriptionService: SubscriptionService,
    private toastr: ToastrService,
    private tokenService: TokenService
  ) {}

  checkAndNotify(): void {
    if (this.checkInFlight || !this.tokenService.hasUsableToken()) {
      return;
    }

    this.checkInFlight = true;
    this.subscriptionService.getUsage().subscribe({
      next: (usage) => {
        this.checkInFlight = false;
        this.showAlerts(usage);
      },
      error: () => {
        this.checkInFlight = false;
      },
    });
  }

  private showAlerts(usage: SubscriptionUsageModel): void {
    const planId = (usage.planId || "").toLowerCase();
    const isPaidPlan = planId === "starter" || planId === "growth";

    const periodKey = this.resolvePeriodKey(usage);
    for (const alert of usage.alerts || []) {
      // Failed-payment alerts matter even after the account has dropped back to the free plan.
      if (isPaidPlan || alert.code.startsWith("payment_failed")) {
        this.showAlert(alert, periodKey);
      }
    }
  }

  private showAlert(alert: SubscriptionUsageAlert, periodKey: string): void {
    const storageKey = `${ALERT_STORAGE_PREFIX}:${periodKey}:${alert.code}`;
    if (sessionStorage.getItem(storageKey)) {
      return;
    }

    const title = alert.code.startsWith("payment_failed")
      ? "Payment failed"
      : alert.code.startsWith("trial_")
        ? "Starter trial"
        : "Subscription usage";
    const options = { timeOut: 12000, closeButton: true };

    switch (alert.level) {
      case "exceeded":
        this.toastr.error(alert.message, title, options);
        break;
      case "warning":
        this.toastr.warning(alert.message, title, options);
        break;
      default:
        this.toastr.info(alert.message, title, options);
        break;
    }

    sessionStorage.setItem(storageKey, "1");
  }

  private resolvePeriodKey(usage: SubscriptionUsageModel): string {
    if (usage.usagePeriodEndUtc) {
      return usage.usagePeriodEndUtc;
    }
    const now = new Date();
    return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
  }
}
